// src/pages/ReviewsPage.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, ThumbsUp, Loader2, Camera } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { useReview, Review } from '@/context/ReviewContext';
import ReviewFormModal from '@/components/ReviewFormModal';
import { cn } from '@/lib/utils';

interface ReviewsPageProps {}

const formatReviewerName = (name: string | null | undefined) => {
  if (!name || name === 'null') return 'Anonymous';
  return name.trim().split(' ')[0];
};

const ReviewStars = ({ rating, size = 16 }: { rating: number; size?: number }) => {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={cn(
            "text-green-600",
            star <= Math.round(rating) ? "fill-current" : "stroke-current opacity-30"
          )}
          style={{ width: size, height: size }}
        />
      ))}
    </div>
  );
};

const RatingDistribution = ({ reviewSummary }: { reviewSummary: any }) => {
  if (!reviewSummary || !reviewSummary.totalReviews) return null;

  const bars = [
    { stars: 5, count: reviewSummary.fiveStarCount || 0 },
    { stars: 4, count: reviewSummary.fourStarCount || 0 },
    { stars: 3, count: reviewSummary.threeStarCount || 0 },
    { stars: 2, count: reviewSummary.twoStarCount || 0 },
    { stars: 1, count: reviewSummary.oneStarCount || 0 },
  ];

  return (
    <div className="space-y-2">
      {bars.map((bar) => {
        const percentage = reviewSummary.totalReviews > 0 
          ? (bar.count / reviewSummary.totalReviews) * 100 
          : 0;
        
        return (
          <div key={bar.stars} className="flex items-center gap-3">
            <span className="text-sm font-medium w-8">{bar.stars}★</span>
            <div className="flex-1 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${percentage}%` }}
              />
            </div>
            <span className="text-sm text-muted-foreground w-12 text-right">
              {Math.round(percentage)}%
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ReviewCard = ({ review }: { review: Review }) => {
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const handleImageClick = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    setImageViewerOpen(true);
  };

  return (
    <Card className="border border-gray-200">
      <CardContent className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">
                {formatReviewerName(review.userName).charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">
                {formatReviewerName(review.userName)}
              </p>
              {review.isVerifiedPurchase && (
                <Badge variant="outline" className="text-xs text-green-600 border-green-600">
                  Verified Purchase
                </Badge>
              )}
            </div>
          </div>
          <div className="text-right">
            <ReviewStars rating={review.rating} />
            <p className="text-sm text-muted-foreground mt-1">
              {new Date(review.createdDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Review Title */}
        {review.reviewTitle && (
          <h3 className="font-semibold text-gray-900 mb-2">
            {review.reviewTitle}
          </h3>
        )}

        {/* Review Comment */}
        {review.comment && (
          <p className="text-gray-700 mb-4 leading-relaxed">
            {review.comment}
          </p>
        )}

        {/* Review Images */}
        {review.imageUrls && review.imageUrls.length > 0 && (
          <div className="mb-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {review.imageUrls.slice(0, 4).map((url, idx) => (
                <button
                  key={`${review.reviewId}-${idx}`}
                  onClick={() => handleImageClick(url)}
                  className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <img
                    src={url}
                    alt={`Review image ${idx + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                </button>
              ))}
              {review.imageUrls.length > 4 && (
                <div className="flex-shrink-0 w-20 h-20 rounded-lg border border-gray-200 flex items-center justify-center bg-gray-50">
                  <span className="text-xs text-gray-500">
                    +{review.imageUrls.length - 4}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Helpful Button */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-gray-700"
          >
            <ThumbsUp className="w-4 h-4 mr-1" />
            Helpful ({review.helpfulCount})
          </Button>
        </div>

        {/* Image Viewer Modal */}
        {imageViewerOpen && selectedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={() => setImageViewerOpen(false)}
          >
            <div className="relative max-w-4xl max-h-4xl p-4">
              <button
                onClick={() => setImageViewerOpen(false)}
                className="absolute -top-12 right-0 text-white hover:text-gray-300 text-xl"
              >
                ✕
              </button>
              <img
                src={selectedImage}
                alt="Review image"
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ReviewsPage: React.FC<ReviewsPageProps> = () => {
  const { productId, productName } = useParams<{
    productId: string;
    productName?: string;
  }>();
  const navigate = useNavigate();
  const [showReviewForm, setShowReviewForm] = useState(false);

  const {
    reviews,
    reviewSummary,
    loading,
    userHasReviewed,
    fetchProductReviews,
    fetchReviewSummary,
    checkUserReviewed,
  } = useReview();

  // Memoize the productId to prevent unnecessary re-renders
  const memoizedProductId = useMemo(() => {
    if (productId) {
      const id = parseInt(productId);
      return !isNaN(id) ? id : null;
    }
    return null;
  }, [productId]);

  // Use useCallback to memoize the fetch functions
  const fetchReviewData = useCallback(async () => {
    if (memoizedProductId) {
      await Promise.all([
        fetchProductReviews(memoizedProductId),
        fetchReviewSummary(memoizedProductId),
        checkUserReviewed(memoizedProductId),
      ]);
    }
  }, [memoizedProductId, fetchProductReviews, fetchReviewSummary, checkUserReviewed]);

  useEffect(() => {
    fetchReviewData();
  }, [fetchReviewData]);

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Loading reviews...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Back Button */}
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Ratings & Reviews</h1>
          {productName && (
            <p className="text-lg text-muted-foreground">{decodeURIComponent(productName)}</p>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Rating Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardContent className="p-6">
                {reviewSummary ? (
                  <>
                    <div className="text-center mb-6">
                      <div className="text-5xl font-bold text-green-600 mb-2">
                        {reviewSummary.averageRating?.toFixed(1) || '0.0'}
                      </div>
                      <ReviewStars rating={reviewSummary.averageRating || 0} size={24} />
                      <p className="text-muted-foreground mt-2">
                        Based on {reviewSummary.totalReviews || 0} reviews
                      </p>
                    </div>

                    <Separator className="mb-6" />

                    <RatingDistribution reviewSummary={reviewSummary} />
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">No reviews yet</p>
                  </div>
                )}

                {!userHasReviewed && (
                  <Button
                    className="w-full mt-6"
                    onClick={() => setShowReviewForm(true)}
                  >
                    Write a Review
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Reviews List */}
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">
                Customer Reviews ({reviews.length})
              </h2>
            </div>

            <div className="space-y-6">
              {reviews.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Star className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">No reviews yet</h3>
                    <p className="text-muted-foreground mb-6">
                      Be the first to share your thoughts about this product.
                    </p>
                    {!userHasReviewed && (
                      <Button onClick={() => setShowReviewForm(true)}>
                        Write the first review
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ) : (
                reviews.map((review) => (
                  <ReviewCard key={review.reviewId} review={review} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Review Form Modal */}
      {showReviewForm && productId && (
        <ReviewFormModal
          isOpen={showReviewForm}
          onClose={() => setShowReviewForm(false)}
          productId={parseInt(productId)}
          productName={productName ? decodeURIComponent(productName) : 'Product'}
        />
      )}
    </Layout>
  );
};

export default ReviewsPage;